import React, { type ComponentProps, useCallback, useEffect } from 'react';
import Animated, {
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fades its content in while lifting it into place, once, when it mounts.
 */
export const Reveal = (props: Props) => {
  const { delay = 0, style, children, ...restProps } = props;

  // ─── State ─────────────────────────────────────────────────────────────────

  const revealProgress = useSharedValue(0);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const startReveal = useCallback(() => {
    revealProgress.value = withDelay(
      delay,
      withTiming(1, REVEAL_TIMING_CONFIG),
      ReduceMotion.System,
    );
  }, [delay, revealProgress]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(startReveal, [startReveal]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const revealStyle = useAnimatedStyle(() => ({
    opacity: revealProgress.value,
    transform: [{ translateY: (1 - revealProgress.value) * REVEAL_DISTANCE }],
  }));
  const containerStyles = [revealStyle, style];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Animated.View {...restProps} style={containerStyles}>
      {children}
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const REVEAL_DISTANCE = 8;
const REVEAL_TIMING_CONFIG = {
  duration: 480,
  easing: Easing.inOut(Easing.quad),
  reduceMotion: ReduceMotion.System,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  delay?: number;
} & ComponentProps<typeof Animated.View>;
